import {Alert, Button, Dialog, openConfirmDialog, useEventListener} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {matchPath, useBlocker} from 'react-router-dom';

import {isNil} from 'ramda';

import {vehiclesRoutes} from '@dms/routes';

import {isNotNilOrEmpty} from 'shared/web';

import {usePricedItemGroup} from '../hooks/usePricedItemGroup';
import {usePublishedPriceSave} from '../hooks/usePublishedPriceSave';
import {PricedItemBatchOutcomeUnknown} from '../types/PricedItemBatchOutcomeUnknown';
import {PricedItemGroupBuildFailure} from '../types/PricedItemGroupBuildFailure';
import {handleCatalogueError, t, UserFacingCatalogueError} from '../utils/catalogueUi';
import {pricedItemGroupStore} from '../utils/pricedItemGroupStore';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';

/** A failed "Review and confirm"; `entryKey` names the entry that could not be built. */
type Failure = {message: string; entryKey?: string};

/** The model year of a path inside one model year: one of its tabs or its configurator preview. */
const modelYearOfPath = (pathname: string) =>
  (
    matchPath(vehiclesRoutes.newVehicleCatalogueModelYear, pathname) ??
    matchPath(vehiclesRoutes.newVehicleCatalogueModelYearPreview, pathname)
  )?.params.modelYearId;

/**
 * The group of price list changes of a published model year (spec 7.2): the button that opens it,
 * then the bar with the count, "Review and confirm" and "Discard". Nothing is sent until Confirm.
 */
export function PricedItemGroupBar(props: {modelYearId: string}) {
  const group = usePricedItemGroup(props.modelYearId);
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const [isReviewing, setReviewing] = useState(false);
  const [failure, setFailure] = useState<Failure>();
  // Leaving the model year loses the group; a tab switch inside the year, or its configurator
  // preview, keeps it (spec 7.2 step 9).
  const leaveBlocker = useBlocker(
    ({nextLocation}) =>
      !isNil(group) && modelYearOfPath(nextLocation.pathname) !== props.modelYearId
  );
  useEventListener(window, 'beforeunload', (event) => {
    if (!isNil(group)) {
      event.preventDefault();
    }
  });
  const leave = () => {
    pricedItemGroupStore.discard(props.modelYearId);
    leaveBlocker.proceed?.();
  };

  const review = () => {
    setFailure(undefined);
    setReviewing(true);
    void publishedPrice
      .confirmPricedItemGroup(props.modelYearId)
      .catch((error: unknown) =>
        match(error)
          // A failed build names its entry, which the user can take out of the group.
          .with(Pattern.instanceOf(PricedItemGroupBuildFailure), (buildFailure) =>
            setFailure({message: buildFailure.message, entryKey: buildFailure.entryKey})
          )
          // The group shows an unknown outcome as long as it holds the batch.
          .with(Pattern.instanceOf(PricedItemBatchOutcomeUnknown), () => undefined)
          .with(Pattern.instanceOf(UserFacingCatalogueError), (refusal) =>
            setFailure({message: refusal.message})
          )
          // A refusal the dialog does not show comes from the server.
          .otherwise(() => handleCatalogueError(error))
      )
      .finally(() => setReviewing(false));
  };
  const removeEntry = (key: string | undefined) => {
    if (!isNil(key)) {
      pricedItemGroupStore.remove(props.modelYearId, key);
    }
    setFailure(undefined);
  };
  const discard = (count: number) =>
    openConfirmDialog({
      text: t(
        'entity.newVehicle.catalogue.itemGroup.discardText',
        'Discard {count} grouped changes? Nothing was saved.',
        {count}
      ),
      onConfirm: () => {
        setFailure(undefined);
        pricedItemGroupStore.discard(props.modelYearId);
      },
    });

  return (
    <>
      <Show when={isNil(group)}>
        <HStack justify="flex-end">
          <Button
            variant="secondary"
            title={t('entity.newVehicle.catalogue.itemGroup.start', 'Group price list changes')}
            onClick={() => pricedItemGroupStore.open(props.modelYearId)}
          />
        </HStack>
      </Show>
      <Show when={group}>
        {(openGroup) => (
          <VStack spacing={2}>
            <Alert
              layout="detailed"
              variant="info"
              type="inline"
              title={t(
                'entity.newVehicle.catalogue.itemGroup.barTitle',
                'Grouped changes: {count}',
                {
                  count: openGroup.entries.length,
                }
              )}
              message={t(
                'entity.newVehicle.catalogue.itemGroup.barHint',
                'Nothing is saved until you confirm. The dealer price list and its label do not change.'
              )}
              actions={[
                {
                  variant: 'primary',
                  title: t('entity.newVehicle.catalogue.itemGroup.review', 'Review and confirm'),
                  isDisabled: openGroup.entries.length === 0,
                  isLoading: isReviewing,
                  onClick: review,
                },
                {
                  variant: 'secondary',
                  title: t('entity.newVehicle.catalogue.itemGroup.discard', 'Discard'),
                  isDisabled: isReviewing,
                  onClick: () => discard(openGroup.entries.length),
                },
              ]}
            />
            <Show when={!isNil(openGroup.unknownOutcome)}>
              <Alert variant="error" message={PricedItemBatchOutcomeUnknown.text()} />
            </Show>
            <Show when={isNotNilOrEmpty(failure?.message) ? failure : null}>
              {(shown) => (
                <Alert
                  layout="detailed"
                  variant="error"
                  type="inline"
                  message={shown.message}
                  actions={
                    isNil(shown.entryKey)
                      ? undefined
                      : [
                          {
                            variant: 'secondary',
                            title: t(
                              'entity.newVehicle.catalogue.itemGroup.removeEntry',
                              'Remove from group'
                            ),
                            onClick: () => removeEntry(shown.entryKey),
                          },
                        ]
                  }
                />
              )}
            </Show>
          </VStack>
        )}
      </Show>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
      <Dialog
        isOpen={leaveBlocker.state === 'blocked'}
        size="small"
        title={t('entity.newVehicle.catalogue.itemGroup.discardTitle', 'Discard grouped changes')}
        onClose={() => leaveBlocker.reset?.()}
        buttons={[
          {
            variant: 'secondary',
            title: t('general.actions.cancel', 'Cancel'),
            onClick: () => leaveBlocker.reset?.(),
          },
          {
            variant: 'danger',
            title: t('general.actions.leave', 'Leave'),
            onClick: leave,
          },
        ]}
      >
        <Text>
          {t(
            'entity.newVehicle.catalogue.itemGroup.leaveText',
            'You have grouped changes that are not saved. Leave and lose them?'
          )}
        </Text>
      </Dialog>
    </>
  );
}
